/* Section Rendering API driver for oddit-section-main-collection.
   - Listens (via delegation on the section root) to filter form changes,
     sort dropdown changes, pagination clicks, and form submits.
   - Fetches the section HTML with the new query string and swaps in
     [data-collection-results] AND [data-filter-sidebar] (inner HTML only —
     the outer elements are stable so delegated listeners survive).
   - Updates the browser URL via pushState so filtered views are shareable.
   - Restores active-element focus + sidebar scroll across each swap. */
(function () {
  'use strict';

  function init() {
    document.querySelectorAll('[data-collection-section]').forEach(initSection);
  }

  function initSection(section) {
    var sectionId = section.getAttribute('data-collection-section');
    var results = section.querySelector('[data-collection-results]');
    if (!results || !sectionId) return;

    var abortController = null;

    function getFilterForm() {
      return section.querySelector('[data-filter-form]');
    }

    function getSidebar() {
      return section.querySelector('[data-filter-sidebar]');
    }

    function readParams() {
      var params = new URLSearchParams();
      var filterForm = getFilterForm();
      if (filterForm) {
        var fd = new FormData(filterForm);
        fd.forEach(function (value, key) {
          if (value !== '' && value != null) params.append(key, value);
        });
      }
      var sortSelect = section.querySelector('[data-collection-sort]');
      if (sortSelect && sortSelect.value) {
        params.delete('sort_by');
        params.set('sort_by', sortSelect.value);
      }
      return params;
    }

    function buildFilterUrl() {
      var url = new URL(window.location.href);
      url.search = readParams().toString();
      return url;
    }

    /* Capture identity of the focused element so we can restore focus after
       the sidebar's innerHTML is replaced (focus would otherwise jump to
       <body>). For inputs we key on [name + value]; for selects on [name]. */
    function captureFocus() {
      var el = document.activeElement;
      if (!el || el === document.body) return null;
      if (!section.contains(el)) return null;
      var tag = el.tagName.toLowerCase();
      var info = { tag: tag, name: el.name || null };
      if (tag === 'input' && (el.type === 'checkbox' || el.type === 'radio')) {
        info.value = el.value;
      }
      return info;
    }

    function restoreFocus(info) {
      if (!info || !info.name) return;
      var selector;
      if (info.value != null) {
        selector = info.tag + '[name="' + cssEscape(info.name) + '"][value="' + cssEscape(info.value) + '"]';
      } else {
        selector = info.tag + '[name="' + cssEscape(info.name) + '"]';
      }
      var match = section.querySelector(selector);
      if (match) match.focus({ preventScroll: true });
    }

    function cssEscape(s) {
      return String(s).replace(/(["\\\]])/g, '\\$1');
    }

    function applyUrl(url, opts) {
      opts = opts || {};
      var fetchUrl = new URL(url.toString());
      fetchUrl.searchParams.set('section_id', sectionId);

      if (abortController) abortController.abort();
      abortController = new AbortController();

      var sidebar = getSidebar();
      var focusInfo = captureFocus();
      var sidebarScroll = sidebar ? sidebar.scrollTop : 0;

      section.classList.add('is-loading');
      return fetch(fetchUrl.toString(), { signal: abortController.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var newResults = doc.querySelector('[data-collection-results]');
          if (newResults) {
            results.innerHTML = newResults.innerHTML;
            results.querySelectorAll('.oddit-reveal').forEach(function (el) {
              el.classList.add('is-visible');
            });
          }

          if (sidebar) {
            var newSidebar = doc.querySelector('[data-filter-sidebar]');
            if (newSidebar) {
              sidebar.innerHTML = newSidebar.innerHTML;
              sidebar.scrollTop = sidebarScroll;
              restoreFocus(focusInfo);
            }
          }

          if (!opts.skipPushState) {
            var cleanUrl = new URL(url.toString());
            cleanUrl.searchParams.delete('section_id');
            window.history.pushState({ collectionFilterUrl: cleanUrl.toString() }, '', cleanUrl.toString());
          }
        })
        .catch(function (err) {
          if (err && err.name !== 'AbortError') {
            console.error('[oddit-section-main-collection]', err);
          }
        })
        .finally(function () {
          section.classList.remove('is-loading');
        });
    }

    /* All event wiring uses delegation on the (stable) section element so
       listeners survive innerHTML swaps inside results and sidebar. */

    section.addEventListener('change', function (e) {
      var target = e.target;
      if (!target) return;
      if (target.matches && target.matches('[data-collection-sort]')) {
        applyUrl(buildFilterUrl());
        return;
      }
      if (target.closest && target.closest('[data-filter-form]')) {
        applyUrl(buildFilterUrl());
      }
    });

    section.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form) return;
      if (form.matches && (form.matches('[data-filter-form]') || form.classList.contains('oddit-section-main-collection__sort-form'))) {
        e.preventDefault();
        applyUrl(buildFilterUrl());
      }
    });

    section.addEventListener('click', function (e) {
      var link = e.target.closest('[data-collection-pagination] a');
      if (link && link.href) {
        e.preventDefault();
        applyUrl(new URL(link.href, window.location.origin));
      }
    });

    window.addEventListener('popstate', function () {
      applyUrl(new URL(window.location.href), { skipPushState: true });
    });

    function openFilters() {
      section.classList.add('is-filter-open');
      var toggle = section.querySelector('[data-filter-open]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeFilters() {
      section.classList.remove('is-filter-open');
      var toggle = section.querySelector('[data-filter-open]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    section.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-filter-open]')) {
        e.preventDefault();
        openFilters();
        return;
      }
      if (e.target.closest && e.target.closest('[data-filter-close]')) {
        e.preventDefault();
        closeFilters();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && section.classList.contains('is-filter-open')) {
        closeFilters();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
